/**
 * WHO-5531. Transport seam for the account Dashboard.
 *
 * WHY NOT APOLLO
 * `graphql-types.codegen.ts` validates every `gql` template and `.graphql`
 * document in `src/` against the LIVE production gateway schema. The
 * `accountDashboard` query and `retryPostalShipmentLabel` mutation are being
 * built in a parallel gateway PR and are deployed nowhere yet. One document
 * naming them would fail `yarn graphql-codegen`, which fails `yarn test`,
 * which is the first half of the Vercel build command, which removes the
 * preview deployment from every open PR in this repo.
 *
 * So, exactly like `src/features/pro-workspace/data/gateway-client.ts` did
 * for WS-9, the operation text lives in plain string constants and this
 * module posts them itself with the same endpoint and bearer token the Apollo
 * client uses (`GRAPHQL_ENDPOINT`, `authStore.token`).
 *
 * Once the gateway query is live in production, convert both operations to
 * `.graphql` documents under `src/Apollo/` and delete this file and types.ts.
 *
 * `createFeedback` and `stripeAccountDetails` already exist in production and
 * keep using their existing Apollo hooks.
 */
import { GRAPHQL_ENDPOINT } from "@/config";
import {
  AccountDashboard,
  AccountDashboardQueryData,
  DASHBOARD_BLOCKS,
  DashboardBlock,
  DashboardUnavailableReason,
  RetryPostalShipmentLabelData,
} from "@/features/account-dashboard/data/types";

type GraphQlError = {
  message: string;
  extensions?: { code?: string } | null;
  path?: ReadonlyArray<string | number> | null;
};

type GraphQlResponse<TData> = {
  data?: TData | null;
  errors?: GraphQlError[] | null;
};

export type DashboardResult<TData> =
  | {
      ok: true;
      data: TData;
      /**
       * Errors that came with usable data. Only requests made with
       * `acceptPartial` return them; the others fail on any error.
       */
      errors?: GraphQlError[];
    }
  | {
      ok: false;
      reason: DashboardUnavailableReason;
      message?: string;
      /** `errors[0].extensions.code` when the gateway sent one (WHO-5688). */
      code?: string;
    };

type DashboardRequest = {
  /** Operation text. A plain string, deliberately not a `gql` template. */
  operation: string;
  variables?: Record<string, unknown>;
  token: string | null;
  timeoutMs?: number;
  /**
   * WHO-5981. Return the data the gateway did send together with the errors,
   * instead of failing the whole request on the first error.
   */
  acceptPartial?: boolean;
};

const DEFAULT_TIMEOUT_MS = 10_000;

/**
 * WHO-5981. The dashboard aggregate reads many sources; a large account must
 * not fail every block on the client clock while the gateway is still working.
 */
const DASHBOARD_TIMEOUT_MS = 25_000;

const isSchemaMissing = (errors: GraphQlError[]) =>
  errors.some(error => {
    if (error.extensions?.code === "GRAPHQL_VALIDATION_FAILED") {
      return true;
    }

    return /cannot query field|unknown (?:type|argument)|has no field/i.test(
      error.message ?? ""
    );
  });

const isForbidden = (errors: GraphQlError[]) =>
  errors.some(error => {
    const code = error.extensions?.code;

    if (code === "FORBIDDEN" || code === "UNAUTHENTICATED") {
      return true;
    }

    return /forbidden|not authenticated|unauthorized/i.test(
      error.message ?? ""
    );
  });

const classify = (errors: GraphQlError[]): DashboardUnavailableReason => {
  if (isSchemaMissing(errors)) {
    return "schema_missing";
  }

  if (isForbidden(errors)) {
    return "forbidden";
  }

  return "transport";
};

/**
 * Issues one operation against the gateway. Client-side only: the dashboard
 * renders behind `auth: "user"` and reads the token from the auth store.
 */
export const dashboardRequest = async <TData>({
  operation,
  variables,
  token,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  acceptPartial = false,
}: DashboardRequest): Promise<DashboardResult<TData>> => {
  if (!token) {
    return { ok: false, reason: "signed_out" };
  }

  const controller =
    typeof AbortController === "undefined" ? null : new AbortController();
  const timer = controller
    ? setTimeout(() => controller.abort(), timeoutMs)
    : null;

  try {
    const response = await fetch(GRAPHQL_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ query: operation, variables: variables ?? {} }),
      signal: controller?.signal,
    });

    if (response.status === 401 || response.status === 403) {
      return { ok: false, reason: "forbidden" };
    }

    if (response.status === 400) {
      // Apollo answers a document it cannot validate (a field this gateway does not have yet)
      // with 400 and the errors in the body: classify them, so callers can fall back.
      const refused = (await response
        .json()
        .catch(() => null)) as GraphQlResponse<TData> | null;
      const errors = refused?.errors ?? [];

      return errors.length > 0
        ? {
            ok: false,
            reason: classify(errors),
            message: errors[0]?.message,
            code: errors[0]?.extensions?.code ?? undefined,
          }
        : { ok: false, reason: "transport" };
    }

    if (!response.ok) {
      return { ok: false, reason: "transport" };
    }

    const payload = (await response.json()) as GraphQlResponse<TData>;
    const errors = payload.errors ?? [];

    const hasData = payload.data !== null && payload.data !== undefined;

    if (errors.length > 0 && acceptPartial && hasData && !isForbidden(errors)) {
      return { ok: true, data: payload.data as TData, errors };
    }

    if (errors.length > 0) {
      return {
        ok: false,
        reason: classify(errors),
        message: errors[0]?.message,
        code: errors[0]?.extensions?.code ?? undefined,
      };
    }

    if (!hasData) {
      return { ok: false, reason: "transport" };
    }

    return { ok: true, data: payload.data as TData };
  } catch {
    return { ok: false, reason: "transport" };
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
};

/* -- operations ------------------------------------------------------------ */

const dashboardOperation = (activityExtras: string) =>
  "query AccountDashboard {" +
  " accountDashboard {" +
  " openActions {" +
  " id type priority title amount orderNumber count deadline occurredAt" +
  " orderId orderGroupId productId threadId bidId" +
  " labelFailure { category message failedAt }" +
  " labelUrl" +
  " }" +
  " kpis { activeAdsCount openBidsCount expectedPayout soldThisYearCount }" +
  " recentActivity {" +
  " id type occurredAt title amount orderNumber orderId orderGroupId productId" +
  activityExtras +
  " }" +
  " hasListings hasPurchases" +
  " } }";

/** With the favorites rows' extras (gateway, 10-07). */
export const ACCOUNT_DASHBOARD_OPERATION = dashboardOperation(
  " count previousAmount"
);

/** For a gateway without those fields (the testing gateway lags production). */
const ACCOUNT_DASHBOARD_LEGACY_OPERATION = dashboardOperation("");

export const RETRY_POSTAL_SHIPMENT_LABEL_OPERATION =
  "mutation RetryPostalShipmentLabel($orderId: String!) {" +
  " retryPostalShipmentLabel(orderId: $orderId) {" +
  " labelUrl failedToSend labelFailure { category message failedAt }" +
  " } }";

export type AccountDashboardResult =
  | {
      ok: true;
      data: AccountDashboard;
      /** Blocks that failed on the gateway; they render their own error. */
      failedBlocks: DashboardBlock[];
    }
  | Extract<DashboardResult<never>, { ok: false }>;

/**
 * WHO-5981. One failing block no longer fails the dashboard: the gateway
 * nulls only that block, and this keeps the rest. A failed block is reported
 * in `failedBlocks` and given an empty value so the shape stays non-null for
 * the components. The whole request only fails when nothing usable came back.
 */
export const fetchAccountDashboard = async (
  token: string | null
): Promise<AccountDashboardResult> => {
  const request = (operation: string) =>
    dashboardRequest<AccountDashboardQueryData>({
      operation,
      token,
      timeoutMs: DASHBOARD_TIMEOUT_MS,
      acceptPartial: true,
    });
  const first = await request(ACCOUNT_DASHBOARD_OPERATION);
  const result =
    !first.ok && first.reason === "schema_missing"
      ? await request(ACCOUNT_DASHBOARD_LEGACY_OPERATION)
      : first;

  if (!result.ok) {
    return result;
  }

  const payload = result.data.accountDashboard;
  const errors = result.errors ?? [];

  if (!payload) {
    return {
      ok: false,
      reason: errors.length > 0 ? classify(errors) : "transport",
      message: errors[0]?.message,
      code: errors[0]?.extensions?.code ?? undefined,
    };
  }

  // A block is failed when the gateway nulled it. An error deeper inside a
  // block (a nullable field of one row) leaves the block usable.
  const failedBlocks = DASHBOARD_BLOCKS.filter(
    block => payload[block] === null || payload[block] === undefined
  );

  return {
    ok: true,
    failedBlocks,
    data: {
      ...payload,
      openActions: payload.openActions ?? [],
      kpis: payload.kpis ?? {
        activeAdsCount: 0,
        openBidsCount: 0,
        expectedPayout: 0,
        soldThisYearCount: 0,
      },
      recentActivity: payload.recentActivity ?? [],
    },
  };
};

export const retryPostalShipmentLabel = async (
  token: string | null,
  orderId: string
): Promise<DashboardResult<RetryPostalShipmentLabelData>> => {
  return dashboardRequest<RetryPostalShipmentLabelData>({
    operation: RETRY_POSTAL_SHIPMENT_LABEL_OPERATION,
    variables: { orderId },
    token,
  });
};
